import type { RideTrack } from './model';

export interface CourseSample { x: number; y: number; z: number; tx: number; ty: number; tz: number; grade: number; curvature: number }
export interface JumpFeature { distance: number; approach: number; landing: number; height: number; angle: number }
export interface RideCourse { sample(distance: number): CourseSample; jumps: JumpFeature[] }
const clamp = (n: number, a: number, b: number) => Math.max(a, Math.min(b, n));
export function jumpsFor(track: RideTrack): JumpFeature[] {
  if (track.discipline === 'road') return [];
  if (track.training && track.discipline === 'mtb') return [];
  const fractions = track.discipline === 'bmx' ? [.03, .105, .18, .255, .53, .605, .68, .755] : [.14, .36, .59, .81];
  return fractions.map((u, i) => {
    const height = (track.discipline === 'bmx' ? 5 + i % 3 * 1.2 : 2.8 + i % 2) * (track.training ? .5 : 1);
    const approach = track.discipline === 'bmx' ? 20 : 18;
    return { distance: track.length * u, approach, landing: track.discipline === 'bmx' ? 65 : 55, height, angle: Math.atan(height / approach * 2) };
  });
}
/** Geometry and physics read the same elevation, including the actual takeoff lip. */
export function elevationAt(track: RideTrack, distance: number): number {
  const d = clamp(distance, 0, track.length), u = d / track.length;
  let y = track.discipline === 'mtb' ? 28 + track.elevation * (1 - u) + 5 * Math.sin(u * Math.PI * 12) + 2 * Math.sin(u * Math.PI * 30)
    : track.discipline === 'bmx' ? 16 + 5 * Math.sin(u * Math.PI * 4) + 2 * Math.sin(u * Math.PI * 8)
    : 22 + 14 * Math.sin(u * Math.PI * 4) + 4 * Math.sin(u * Math.PI * 8);
  if (track.training) y = track.discipline === 'mtb' ? 28 + track.elevation * (1 - u) + .3 * Math.sin(u * Math.PI * 6) : 22 + (track.discipline === 'road' ? 3.5 : .8) * Math.sin(u * Math.PI * 4);
  for (const jump of jumpsFor(track)) {
    const delta = d - jump.distance;
    if (delta > -jump.approach && delta <= 0) y += jump.height * ((delta + jump.approach) / jump.approach) ** 2;
    if (delta > 0 && delta < jump.landing) y += jump.height * (1 - delta / jump.landing) ** 2;
  }
  return y;
}
export function createCourse(track: RideTrack): RideCourse {
  // Arc-length resampling keeps one game metre equal to one horizontal world metre.
  const path: { x: number; z: number; distance: number }[] = [];
  for (let i = 0; i <= 2000; i++) {
    const u = i / 2000, a = u * Math.PI * 2;
    let x = track.discipline === 'mtb' ? Math.sin(u * Math.PI * 3) * 100 + Math.sin(u * Math.PI * 9) * 20
      : Math.sin(a) * (track.discipline === 'bmx' ? 100 : 330) * (track.discipline === 'road' ? 1 + .07 * Math.sin(a * 3) : 1);
    let z = track.discipline === 'mtb' ? (u - .5) * 2100 : Math.cos(a) * (track.discipline === 'bmx' ? 230 : 440);
    if (track.training && track.discipline === 'mtb') x *= .15;
    if (track.discipline === 'bmx') {
      const radius = 60, straight = 400, arc = Math.PI * radius, s = u * (straight * 2 + arc * 2);
      if (s < straight) { x = -radius; z = -straight / 2 + s; }
      else if (s < straight + arc) { const angle = (s - straight) / radius; x = -Math.cos(angle) * radius; z = straight / 2 + Math.sin(angle) * radius; }
      else if (s < straight * 2 + arc) { x = radius; z = straight / 2 - (s - straight - arc); }
      else { const angle = (s - straight * 2 - arc) / radius; x = Math.cos(angle) * radius; z = -straight / 2 - Math.sin(angle) * radius; }
    }
    const prev = path[i - 1]; path.push({ x, z, distance: prev ? prev.distance + Math.hypot(x - prev.x, z - prev.z) : 0 });
  }
  const total = path[2000].distance, scale = track.length / total;
  const raw = (distance: number) => {
    const d = clamp(distance, 0, track.length) / scale;
    let low = 0, high = 2000;
    while (high - low > 1) { const mid = (low + high) >> 1; if (path[mid].distance < d) low = mid; else high = mid; }
    const a = path[low], b = path[high], t = (d - a.distance) / (b.distance - a.distance);
    return { x: (a.x + (b.x - a.x) * t) * scale, z: (a.z + (b.z - a.z) * t) * scale };
  };
  return { jumps: jumpsFor(track), sample(distance) {
    const d = clamp(distance, 0, track.length), p = raw(d), a = raw(d - .6), b = raw(d + .6);
    const span = Math.max(.001, Math.min(track.length, d + .6) - Math.max(0, d - .6));
    const grade = (elevationAt(track, d + .6) - elevationAt(track, d - .6)) / span;
    const len = Math.hypot(b.x - a.x, b.z - a.z) || 1;
    const prev = raw(d - 3), next = raw(d + 3);
    const ax = p.x - prev.x, az = p.z - prev.z, bx = next.x - p.x, bz = next.z - p.z;
    const curvature = (ax * bz - az * bx) / Math.max(.01, Math.hypot(ax, az) * Math.hypot(bx, bz) * 3);
    return { ...p, y: elevationAt(track, d), tx: (b.x - a.x) / len, ty: grade / Math.sqrt(1 + grade * grade), tz: (b.z - a.z) / len, grade, curvature };
  } };
}
